// LocaleSync — однократно устраняет рассинхрон языка UI ↔ users.language
// после логина (cold start / новое устройство / уже разъехавшееся состояние).
//
// Направление синхронизации зависит от того, был ли язык выбран явно на этом
// устройстве (HAD_STORED_LOCALE):
//   - Явный выбор был → побеждает выбор устройства: подтягиваем users.language
//     к текущему UI (серверный контент — рекомендации, уведомления, AI — станет
//     на языке интерфейса). Это чинит ситуацию со скриншота: UI на en, БД на ru.
//   - Первый визит (язык определён autodetect'ом по navigator) → источник истины
//     БД: переключаем UI на users.language (выбор, сделанный на другом устройстве).
//
// Дальше usePersistLocale держит обе стороны синхронными при каждом переключении.
// Рендерит null — монтируется один раз в __root.

import { useEffect, useRef } from "react";

import { usePersistLocale } from "@/hooks/use-persist-locale";
import { useSession } from "@/hooks/use-session";
import { HAD_STORED_LOCALE, URL_LOCALE } from "@/i18n";
import { normalizeLocale } from "@/i18n/locales";
import { useLocale } from "@/i18n/use-locale";

export function LocaleSync(): null {
	const { user, isOffline } = useSession();
	const { locale, setLocale } = useLocale();
	const persistLocale = usePersistLocale();
	const done = useRef(false);

	const dbLanguage = user?.language;
	const userId = user?.id;

	useEffect(() => {
		// Сессия открыта по языковому адресу (`/de/about`) — синхронизацию не
		// запускаем ВООБЩЕ, до следующей загрузки без префикса.
		//
		// Причина: язык интерфейса здесь задан адресом, а не человеком. Ветка
		// «явный выбор победил» записала бы язык случайной ссылки в users.language,
		// то есть переход по немецкой ссылке молча переключил бы на немецкий и
		// рекомендации, и письма. Ветка «БД победила» перекрасила бы публичную
		// страницу вопреки её адресу — ссылка на языковую версию перестала бы
		// работать.
		//
		// Язык при этом не теряется: его возвращает `LocaleRouteSync` — гостю при
		// уходе с публичной страницы, вошедшему сразу, как только подтвердится
		// сессия (языковой адрес есть только у гостя, см. `lib/locale-url.ts`).
		// Отсюда же и порядок: гонки «кто первый запишет users.language» между
		// двумя компонентами не возникает, потому что этот просто не работает на
		// языковом адресе.
		if (URL_LOCALE) return;
		// Один раз за сессию, только при подтверждённой онлайн-сессии с языком в БД.
		if (done.current || isOffline || !userId || !dbLanguage) return;
		done.current = true;

		const normalizedDb = normalizeLocale(dbLanguage);
		if (normalizedDb === locale) return; // уже согласованы

		if (HAD_STORED_LOCALE) {
			// Явный выбор на устройстве победил → синхронизируем БД (UI не трогаем).
			void persistLocale(locale);
		} else {
			// Первый визит → БД источник истины для UI. Именно `setLocale`, а не голый
			// `changeLanguage`: detector больше не кэширует язык сам (см. `i18n/index.ts`,
			// `detection.caches: []`), а выбор с другого устройства — такой же явный
			// выбор, и сервер обязан его видеть (localStorage + кука).
			void setLocale(normalizedDb);
		}
	}, [userId, dbLanguage, isOffline, locale, persistLocale, setLocale]);

	return null;
}
